import { Box, Typography } from '@mui/material';
import type { SxProps } from '@mui/material';
import { useEffect, useState } from 'react';
import { isAllowedMimeType } from './viewerHtml';
import ZoomableImageFrame from './ZoomableImageFrame';

export type SandboxedFileViewerProps = {
  fileUrl: string;
  mimeType: string;
  sx?: SxProps;
  /** When true, images render with a zoom + pan control strip. Ignored for PDF/video. */
  enableZoom?: boolean;
};

type ViewerState = 'loading' | 'ready' | 'error' | 'mismatch' | 'unsupported';

const MAGIC_BYTES: Record<string, { offset: number; bytes: number[] }> = {
  'image/png': { offset: 0, bytes: [0x89, 0x50, 0x4e, 0x47] },
  'image/jpeg': { offset: 0, bytes: [0xff, 0xd8, 0xff] },
  'application/pdf': { offset: 0, bytes: [0x25, 0x50, 0x44, 0x46] },
  'video/mp4': { offset: 4, bytes: [0x66, 0x74, 0x79, 0x70] },
  'video/webm': { offset: 0, bytes: [0x1a, 0x45, 0xdf, 0xa3] },
};

function matchesMagicBytes(buffer: ArrayBuffer, mimeType: string): boolean {
  // S3 may store video content whose format doesn't match the file
  // extension (e.g. WebM content with .mp4 extension from browser
  // recording). Accept any known video signature for any video MIME.
  const isVideo = mimeType.startsWith('video/');
  const candidates = isVideo
    ? Object.entries(MAGIC_BYTES).filter(([k]) => k.startsWith('video/'))
    : [[mimeType, MAGIC_BYTES[mimeType]] as const];

  return candidates.some(([, sig]) => {
    if (!sig) return false;
    if (buffer.byteLength < sig.offset + sig.bytes.length) return false;
    const view = new Uint8Array(buffer, sig.offset, sig.bytes.length);
    return sig.bytes.every((b, i) => view[i] === b);
  });
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const CHUNK = 0x8000;
  const chunks: string[] = [];
  for (let i = 0; i < bytes.length; i += CHUNK) {
    chunks.push(String.fromCharCode(...bytes.subarray(i, i + CHUNK)));
  }
  return btoa(chunks.join(''));
}

function buildImageVideoSrcdoc(dataUri: string, mimeType: string, fillContainer = false): string {
  let element: string;
  if (mimeType.startsWith('image/')) {
    element = `<img src="${dataUri}" alt="Document preview">`;
  } else {
    element = `<video src="${dataUri}" controls controlsList="nodownload noremoteplayback" disablePictureInPicture preload="metadata"></video>`;
  }

  const isVideo = mimeType.startsWith('video/');
  const bg = isVideo ? '#000' : 'transparent';

  // Two image sizing modes:
  // - default (fit): the image renders at its intrinsic size, shrunk to fit the
  //   viewport. Correct for a fixed-size viewer.
  // - fillContainer (zoom): the image fills the viewport (object-fit keeps aspect),
  //   so when the parent grows the iframe box the browser re-rasterises the image
  //   larger from source. This is what makes resize-based zoom actually enlarge the
  //   picture; max-width/max-height alone would cap it at intrinsic size.
  const imgStyle = fillContainer
    ? 'img { width: 100%; height: 100%; object-fit: contain; display: block; }'
    : 'img { max-width: 100%; max-height: 100vh; object-fit: contain; }';

  return `<!DOCTYPE html>
<html>
<head>
<meta http-equiv="Content-Security-Policy" content="script-src 'none'; img-src data:; media-src data:;">
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { display: flex; align-items: center; justify-content: center; width: 100%; height: 100vh; overflow: hidden; background: ${bg}; }
  ${imgStyle}
  video { width: 100%; aspect-ratio: 4/3; display: block; object-fit: cover; }
</style>
</head>
<body>${element}</body>
</html>`;
}

export default function SandboxedFileViewer({
  fileUrl,
  mimeType,
  sx,
  enableZoom = false,
}: Readonly<SandboxedFileViewerProps>) {
  const [state, setState] = useState<ViewerState>(() =>
    isAllowedMimeType(mimeType) ? 'loading' : 'unsupported',
  );
  const [contentData, setContentData] = useState<{ buffer: ArrayBuffer; blobUrl: string } | null>(
    null,
  );

  const isPdf = mimeType === 'application/pdf';

  useEffect(() => {
    if (!isAllowedMimeType(mimeType)) {
      setState('unsupported');
      return;
    }

    let cancelled = false;
    setState('loading');
    setContentData(null);

    fetch(fileUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.arrayBuffer();
      })
      .then((buffer) => {
        if (cancelled) return;
        if (!matchesMagicBytes(buffer, mimeType)) {
          console.error('SandboxedFileViewer: file content does not match declared MIME type');
          setState('mismatch');
          return;
        }
        if (isPdf) {
          const blob = new Blob([buffer], { type: 'application/pdf' });
          setContentData({ buffer, blobUrl: URL.createObjectURL(blob) });
        } else {
          setContentData({ buffer, blobUrl: '' });
        }
        setState('ready');
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('SandboxedFileViewer: failed to load file', error);
        setState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [fileUrl, mimeType, isPdf]);

  useEffect(() => {
    return () => {
      if (contentData?.blobUrl) URL.revokeObjectURL(contentData.blobUrl);
    };
  }, [contentData]);

  if (state === 'unsupported') {
    return (
      <Box sx={sx} display="flex" alignItems="center" justifyContent="center">
        <Typography color="text.secondary">Unsupported file type</Typography>
      </Box>
    );
  }

  if (state === 'error') {
    return (
      <Box sx={sx} display="flex" alignItems="center" justifyContent="center">
        <Typography color="error">Failed to load file</Typography>
      </Box>
    );
  }

  if (state === 'mismatch') {
    return (
      <Box sx={sx} display="flex" alignItems="center" justifyContent="center">
        <Typography color="error">File format does not match the expected type</Typography>
      </Box>
    );
  }

  if (state === 'ready' && contentData) {
    if (isPdf) {
      // PDFs use <iframe src={blobUrl}> without sandbox because Chrome's
      // built-in PDF viewer requires internal script execution, which
      // sandbox="" denies. The blob has explicit type 'application/pdf'
      // to prevent content-sniffing, and browser PDF viewers are
      // process-isolated (Chrome) or origin-scoped (Firefox/pdf.js).
      return (
        <Box sx={sx}>
          <iframe
            src={contentData.blobUrl}
            title="Sandboxed file viewer"
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </Box>
      );
    }

    // Images and video use data: URIs inside a fully locked-down iframe:
    // - sandbox="" (empty = all restrictions, opaque origin, no scripts)
    // - CSP script-src 'none' (belt-and-suspenders)
    // Even if content were somehow interpreted as HTML, the opaque origin
    // prevents access to parent cookies/localStorage/DOM, and CSP blocks
    // all script execution.
    const base64 = arrayBufferToBase64(contentData.buffer);
    const dataUri = `data:${mimeType};base64,${base64}`;
    const srcdoc = buildImageVideoSrcdoc(dataUri, mimeType);

    const isVideo = mimeType.startsWith('video/');

    if (!isVideo && enableZoom) {
      // The zoom path uses a fill-container srcdoc so the image scales up with the
      // iframe box (see buildImageVideoSrcdoc). Same sandbox="" + CSP as every path.
      const zoomSrcdoc = buildImageVideoSrcdoc(dataUri, mimeType, true);
      return (
        <Box sx={sx}>
          <ZoomableImageFrame srcdoc={zoomSrcdoc} />
        </Box>
      );
    }

    return (
      <Box sx={sx}>
        <iframe
          sandbox=""
          srcDoc={srcdoc}
          title="Sandboxed file viewer"
          style={{
            width: '100%',
            height: isVideo ? undefined : '100%',
            aspectRatio: isVideo ? '4/3' : undefined,
            display: 'block',
            border: 'none',
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={sx} position="relative">
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    </Box>
  );
}
